// Class API service — real API calls replacing mock data

import { apiRequest } from './api';

// ── Raw API response types ──────────────────────────────────

export type ClassApiRecord = {
    id: string;
    organization_id: string;
    school_id: string;
    name: string;
    standard: number;
    has_sections: boolean;
    capacity: number | null;
    class_teacher_id: string | null;
    is_active: boolean;
    metadata: Record<string, unknown> | null;
    created_at: string;
    updated_at: string;
    sections: SectionApiRecord[];
};

export type ClassPayload = {
    name: string;
    school_id:string | null;
    standard: number;
    capacity: number | null;
    has_sections: boolean;
    class_teacher_id: string | null;
    is_active: boolean;
    metadata: Record<string, unknown> | null;
};

export type ClassListResponse = {
    success: boolean;
    message: string;
    data: ClassApiRecord[];
    meta: {
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
    };
};

// ── API Functions ───────────────────────────────────────────

/**
 * List classes with optional pagination.
 */
export async function fetchClasses(params?: {
    page?: number;
    perPage?: number;
}): Promise<ClassListResponse> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.perPage) query.set('per_page', String(params.perPage));

    const qs = query.toString();
    const res = await apiRequest<ClassApiRecord[]>(`/api/classes${qs ? `?${qs}` : ''}`);

    return {
        success: res.success,
        message: res.message,
        data: res.data,
        meta: (res as unknown as ClassListResponse).meta,
    };
}

/**
 * Create a new class.
 */
export async function createClass(payload: ClassPayload): Promise<ClassApiRecord> {
    const res = await apiRequest<ClassApiRecord>('/api/classes', {
        method: 'POST',
        body: JSON.stringify(payload),
    });
    return res.data;
}

/**
 * Update an existing class.
 */
export async function updateClass(id: string, payload: ClassPayload): Promise<ClassApiRecord> {
    const res = await apiRequest<ClassApiRecord>(`/api/classes/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
    });
    return res.data;
}

// ── Section types & API ─────────────────────────────────────

export type SectionApiRecord = {
    id: string;
    organization_id: string;
    class_id: string;
    name: string;
    capacity: number;
    strength: number;
    class_teacher_id: string | null;
    is_active: boolean;
    created_at: string;
    updated_at: string;
    students_count: number;
};

export type SectionPayload = {
    name: string;
    capacity: number;
    class_teacher_id?: string | null;
    strength?: number;
    is_active?: boolean;
};

export type SectionListResponse = {
    success: boolean;
    message: string;
    data: SectionApiRecord[];
    meta: {
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
    };
};

/**
 * List sections for a class.
 */
export async function fetchClassSections(classId: string, params?: {
    page?: number;
    perPage?: number;
}): Promise<SectionListResponse> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.perPage) query.set('per_page', String(params.perPage));

    const qs = query.toString();
    const res = await apiRequest<SectionApiRecord[]>(
        `/api/classes/${classId}/sections${qs ? `?${qs}` : ''}`
    );

    return {
        success: res.success,
        message: res.message,
        data: res.data,
        meta: (res as unknown as SectionListResponse).meta,
    };
}

/**
 * Create a section under a class.
 */
export async function createSection(classId: string, payload: SectionPayload): Promise<SectionApiRecord> {
    const res = await apiRequest<SectionApiRecord>(`/api/classes/${classId}/sections`, {
        method: 'POST',
        body: JSON.stringify(payload),
    });
    return res.data;
}

/**
 * Update an existing section.
 */
export async function updateSection(classId: string, sectionId: string, payload: SectionPayload): Promise<SectionApiRecord> {
    const res = await apiRequest<SectionApiRecord>(`/api/classes/${classId}/sections/${sectionId}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
    });
    return res.data;
}

// ── Class subject allocation types & API ────────────────────

export type ClassSubjectRecord = {
    id: string;
    organization_id: string;
    school_id: string;
    class_id: string;
    section_id: string | null;
    school_subject_id: string;
    subject_name: string;
    subject_code: string;
    is_mandatory: boolean;
    weekly_classes_count: number | null;
    credits: string | null;
    created_at: string;
    updated_at: string;
    section?: {
        id: string;
        name: string;
    } | null;
    assigned_teacher?: {
        id: string;
        first_name: string;
        last_name: string | null;
    } | null;
    board_subject?: {
        id: number;
        name: string;
        code: string;
        type: string;
        has_practical: boolean;
        max_marks: number;
    } | null;
};

export type ClassSubjectPayload = {
    school_id: string;
    school_subject_id: string;
    section_id?: string | null;
    assigned_teacher_id?: string | null;
    is_mandatory?: boolean;
    weekly_classes_count?: number;
    credits?: number;
};

export type ClassSubjectListResponse = {
    success: boolean;
    message: string;
    data: ClassSubjectRecord[];
    meta: {
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
    };
};

/**
 * List subject allocations for a class.
 */
export async function fetchClassSubjects(classId: string, params?: {
    page?: number;
    perPage?: number;
    sectionId?: string;
}): Promise<ClassSubjectListResponse> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.perPage) query.set('per_page', String(params.perPage));
    if (params?.sectionId) query.set('section_id', params.sectionId);

    const qs = query.toString();
    const res = await apiRequest<ClassSubjectRecord[]>(
        `/api/classes/${classId}/subjects${qs ? `?${qs}` : ''}`
    );

    return {
        success: res.success,
        message: res.message,
        data: res.data,
        meta: (res as unknown as ClassSubjectListResponse).meta,
    };
}

/**
 * Allocate a subject to a class.
 */
export async function createClassSubject(classId: string, payload: ClassSubjectPayload): Promise<ClassSubjectRecord> {
    const res = await apiRequest<ClassSubjectRecord>(`/api/classes/${classId}/subjects`, {
        method: 'POST',
        body: JSON.stringify(payload),
    });
    return res.data;
}

// ── Mapper ──────────────────────────────────────────────────

/**
 * Map an API class record to the flat UI-friendly shape used by ClassManagement.
 */
export function mapClassToUi(c: ClassApiRecord) {
    return {
        id: c.id,
        name: c.name,
        standard: c.standard,
        has_sections: c.has_sections,
        capacity: c.capacity,
        class_teacher_id: c.class_teacher_id,
        is_active: c.is_active,
        created_at: c.created_at,
        updated_at: c.updated_at,
        sections: c.sections ?? [],
    };
}

export type ClassUi = ReturnType<typeof mapClassToUi>;
